import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Eye, MousePointer, Users, ArrowRight, ArrowUp, ArrowDown,
  Loader2, RefreshCw, Calendar, Globe, Smartphone, Monitor, Tablet, Target,
  Clock, MapPin, ExternalLink, Layers, Zap, Activity, PieChart, Play, Pause,
  CheckCircle2, XCircle, FlaskConical, Percent
} from 'lucide-react';
import { landingPageAPI } from '../api';
import { format, subDays } from 'date-fns';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Treemap, ScatterChart, Scatter
} from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const DATE_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: '90', label: 'Last 90 Days' },
];

// Heatmap visualization component
const HeatmapVisualization = ({ data, width = 400, height = 600 }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !data || data.length === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw background (simulated page)
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw page sections
    const sections = [
      { y: 0, h: 100, label: 'Hero', color: '#e2e8f0' },
      { y: 100, h: 120, label: 'Features', color: '#f1f5f9' },
      { y: 220, h: 100, label: 'Testimonials', color: '#e2e8f0' },
      { y: 320, h: 150, label: 'Form', color: '#f1f5f9' },
      { y: 470, h: 80, label: 'CTA', color: '#e2e8f0' },
    ];
    
    sections.forEach(section => {
      ctx.fillStyle = section.color;
      ctx.fillRect(0, section.y, canvas.width, section.h);
      ctx.fillStyle = '#94a3b8';
      ctx.font = '11px sans-serif';
      ctx.fillText(section.label, 10, section.y + 20);
    });
    
    // Normalize click positions
    const maxX = Math.max(...data.map(d => d.x), 1);
    const maxY = Math.max(...data.map(d => d.y), 1);
    
    // Draw heatmap points
    data.forEach(point => {
      const x = (point.x / maxX) * canvas.width;
      const y = Math.min((point.y / maxY) * canvas.height, canvas.height - 10);
      
      // Create gradient for heat effect
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, 20);
      gradient.addColorStop(0, 'rgba(239, 68, 68, 0.6)');
      gradient.addColorStop(0.5, 'rgba(251, 146, 60, 0.3)');
      gradient.addColorStop(1, 'rgba(251, 191, 36, 0)');
      
      ctx.beginPath();
      ctx.arc(x, y, 20, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
    });
    
    // Draw intensity legend
    ctx.fillStyle = '#64748b';
    ctx.font = '10px sans-serif';
    ctx.fillText('Click Intensity:', canvas.width - 100, 15);
    
    const legendGradient = ctx.createLinearGradient(canvas.width - 100, 25, canvas.width - 20, 25);
    legendGradient.addColorStop(0, '#fbbf24');
    legendGradient.addColorStop(0.5, '#fb923c');
    legendGradient.addColorStop(1, '#ef4444');
    ctx.fillStyle = legendGradient;
    ctx.fillRect(canvas.width - 100, 20, 80, 8);
    
  }, [data]);
  
  return (
    <div className="relative border rounded-lg overflow-hidden bg-slate-50">
      <canvas 
        ref={canvasRef} 
        width={width} 
        height={height}
        className="w-full"
        style={{ maxHeight: '550px' }}
      />
      {(!data || data.length === 0) && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80">
          <p className="text-muted-foreground text-sm">No click data available yet</p>
        </div>
      )}
    </div>
  );
};

export const LandingPageAnalyticsPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [selectedPage, setSelectedPage] = useState('all');
  const [pages, setPages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [pageAnalytics, setPageAnalytics] = useState(null);
  const [abTests, setAbTests] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all landing pages
      const pagesRes = await landingPageAPI.getAll({ limit: 100 });
      const pagesList = pagesRes.data?.pages || pagesRes.data || [];
      setPages(pagesList);
      
      // Fetch analytics based on selection
      if (selectedPage === 'all') {
        // Fetch overview analytics
        const overviewRes = await landingPageAPI.getOverviewAnalytics(parseInt(dateRange));
        setAnalytics(overviewRes.data);
        setPageAnalytics(null);
      } else {
        // Fetch specific page analytics
        const pageRes = await landingPageAPI.getAnalytics(selectedPage, parseInt(dateRange));
        setPageAnalytics(pageRes.data);
        setAnalytics(null);
        
        // Fetch A/B tests for this page
        try {
          const abRes = await landingPageAPI.getABTests(selectedPage);
          setAbTests(abRes.data?.tests || []);
        } catch (e) {
          setAbTests([]);
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
      
      // Fallback to generated data if API fails
      setAnalytics(generateFallbackData(pagesList, parseInt(dateRange)));
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackData = (pagesList, days) => {
    const totalViews = pagesList.reduce((acc, p) => acc + (p.views || 0), 0);
    const totalSubmissions = pagesList.reduce((acc, p) => acc + (p.submissions || 0), 0);
    
    return {
      overview: {
        total_pages: pagesList.length,
        published_pages: pagesList.filter(p => p.status === 'published').length,
        total_views: totalViews,
        total_submissions: totalSubmissions,
        overall_conversion_rate: totalViews > 0 ? ((totalSubmissions / totalViews) * 100).toFixed(2) : 0,
      },
      device_breakdown: { desktop: Math.floor(totalViews * 0.55), mobile: Math.floor(totalViews * 0.38), tablet: Math.floor(totalViews * 0.07) },
      top_pages: pagesList.slice(0, 5).map(p => ({
        id: p.id,
        name: p.name,
        views: p.views || 0,
        submissions: p.submissions || 0,
        conversion_rate: p.views > 0 ? ((p.submissions / p.views) * 100).toFixed(2) : 0
      }))
    };
  };

  const StatCard = ({ icon: Icon, label, value, change, changeType, iconColor }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <Icon className={`h-8 w-8 ${iconColor || 'text-blue-500'}`} />
          {change !== undefined && (
            <Badge variant="outline" className={changeType === 'up' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
              {changeType === 'up' ? <ArrowUp className="h-3 w-3 mr-1" /> : <ArrowDown className="h-3 w-3 mr-1" />}
              {change}%
            </Badge>
          )}
        </div>
        <p className="text-2xl font-bold mt-2">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentData = selectedPage === 'all' ? analytics : pageAnalytics;
  const overview = currentData?.overview || {};

  return (
    <div className="space-y-6" data-testid="landing-page-analytics">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Landing Page Analytics
          </h1>
          <p className="text-muted-foreground">
            Track performance, conversions, and visitor engagement with real click tracking
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[150px]" data-testid="date-range-select">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGES.map(range => (
                <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedPage} onValueChange={setSelectedPage}>
            <SelectTrigger className="w-[200px]" data-testid="page-select">
              <Layers className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Pages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pages</SelectItem>
              {pages.map(page => (
                <SelectItem key={page.id} value={page.id}>{page.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <StatCard 
          icon={Eye} 
          label="Total Views" 
          value={overview.total_views || 0} 
          iconColor="text-blue-500"
        />
        <StatCard 
          icon={MousePointer} 
          label={selectedPage === 'all' ? 'Total Submissions' : 'Total Clicks'} 
          value={selectedPage === 'all' ? (overview.total_submissions || 0) : (overview.total_clicks || 0)} 
          iconColor="text-green-500"
        />
        <StatCard 
          icon={Target} 
          label="Conversion Rate" 
          value={`${overview.conversion_rate || overview.overall_conversion_rate || 0}%`} 
          iconColor="text-amber-500"
        />
        <StatCard 
          icon={Users} 
          label={selectedPage === 'all' ? 'Published Pages' : 'Unique Visitors'} 
          value={selectedPage === 'all' ? (overview.published_pages || 0) : (overview.unique_visitors || 0)} 
          iconColor="text-purple-500"
        />
        {selectedPage !== 'all' && (
          <StatCard 
            icon={Clock} 
            label="Avg Time on Page" 
            value={`${Math.round(overview.avg_time_on_page || 0)}s`} 
            iconColor="text-cyan-500"
          />
        )}
      </div>

      {/* Tabs for different analytics views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {selectedPage !== 'all' && (
            <>
              <TabsTrigger value="heatmap">Click Heatmap</TabsTrigger>
              <TabsTrigger value="engagement">Section Engagement</TabsTrigger>
              <TabsTrigger value="abtests">A/B Tests</TabsTrigger>
            </>
          )}
          <TabsTrigger value="devices">Devices</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Performance Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Performance Over Time</CardTitle>
                <CardDescription>Views and conversions trend</CardDescription>
              </CardHeader>
              <CardContent>
                {currentData?.daily_stats?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={currentData.daily_stats}>
                      <defs>
                        <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorSubmissions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="views" stroke="#3B82F6" fillOpacity={1} fill="url(#colorViews)" name="Views" />
                      <Area type="monotone" dataKey="submissions" stroke="#10B981" fillOpacity={1} fill="url(#colorSubmissions)" name="Submissions" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No data available for the selected period
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Conversion Funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Conversion Funnel</CardTitle>
                <CardDescription>Visitor journey from view to conversion</CardDescription>
              </CardHeader>
              <CardContent>
                {currentData?.funnel?.length > 0 ? (
                  <div className="space-y-4">
                    {currentData.funnel.map((stage, idx) => {
                      const maxCount = currentData.funnel[0]?.count || 1;
                      const percentage = ((stage.count / maxCount) * 100).toFixed(1);
                      return (
                        <div key={stage.stage} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{stage.stage}</span>
                            <span className="text-muted-foreground">{stage.count.toLocaleString()} ({percentage}%)</span>
                          </div>
                          <Progress value={parseFloat(percentage)} className="h-8" />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No funnel data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Pages Table (for overview) */}
          {selectedPage === 'all' && currentData?.top_pages?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Performing Pages</CardTitle>
                <CardDescription>Ranked by total views</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {currentData.top_pages.map((page, idx) => (
                    <div 
                      key={page.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                      onClick={() => setSelectedPage(page.id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium">{page.name}</p>
                          <p className="text-sm text-muted-foreground">{page.views?.toLocaleString()} views</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium text-green-600">{page.submissions} conversions</p>
                          <p className="text-sm text-muted-foreground">{page.conversion_rate}% rate</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Heatmap Tab (single page only) */}
        {selectedPage !== 'all' && (
          <TabsContent value="heatmap" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MousePointer className="h-5 w-5" />
                    Click Heatmap
                  </CardTitle>
                  <CardDescription>
                    Visual representation of where visitors click on your page
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <HeatmapVisualization 
                    data={pageAnalytics?.heatmap_data || []} 
                    width={400} 
                    height={550}
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Scroll Depth Analysis</CardTitle>
                  <CardDescription>How far visitors scroll down the page</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>Average Scroll Depth</span>
                        <span className="font-medium">{pageAnalytics?.overview?.avg_scroll_depth || 0}%</span>
                      </div>
                      <Progress value={pageAnalytics?.overview?.avg_scroll_depth || 0} className="h-3" />
                    </div>
                    
                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium mb-3">Scroll Milestones</p>
                      {[25, 50, 75, 100].map(milestone => {
                        const reached = (pageAnalytics?.overview?.avg_scroll_depth || 0) >= milestone;
                        return (
                          <div key={milestone} className="flex items-center gap-2 py-1">
                            {reached ? (
                              <CheckCircle2 className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-slate-300" />
                            )}
                            <span className={`text-sm ${reached ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {milestone}% of page
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-4 border-t">
                      <p className="text-sm font-medium mb-2">Time on Page</p>
                      <p className="text-3xl font-bold text-primary">
                        {Math.round(pageAnalytics?.overview?.avg_time_on_page || 0)}s
                      </p>
                      <p className="text-sm text-muted-foreground">average session duration</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}

        {/* Section Engagement Tab */}
        {selectedPage !== 'all' && (
          <TabsContent value="engagement" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Section Engagement</CardTitle>
                <CardDescription>Click distribution across page sections</CardDescription>
              </CardHeader>
              <CardContent>
                {pageAnalytics?.section_engagement?.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      {pageAnalytics.section_engagement.map((section, idx) => (
                        <div key={section.section} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium capitalize">{section.section}</span>
                            <span className="text-muted-foreground">
                              {section.clicks.toLocaleString()} clicks ({section.engagement}%)
                            </span>
                          </div>
                          <Progress 
                            value={section.engagement} 
                            className="h-6"
                            style={{ 
                              '--progress-background': COLORS[idx % COLORS.length] 
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <div>
                      <ResponsiveContainer width="100%" height={300}>
                        <RechartsPie>
                          <Pie
                            data={pageAnalytics.section_engagement}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="clicks"
                            nameKey="section"
                            label={({ section, percent }) => `${section} ${(percent * 100).toFixed(0)}%`}
                          >
                            {pageAnalytics.section_engagement.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No engagement data available yet. Click tracking will populate this once visitors interact with the page.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* A/B Tests Tab */}
        {selectedPage !== 'all' && (
          <TabsContent value="abtests" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FlaskConical className="h-5 w-5" />
                    A/B Tests
                  </CardTitle>
                  <CardDescription>Test different page variations</CardDescription>
                </div>
                <Button onClick={() => toast.info('Create A/B test from Landing Page Builder')}>
                  New A/B Test
                </Button>
              </CardHeader>
              <CardContent>
                {abTests.length > 0 ? (
                  <div className="space-y-4">
                    {abTests.map(test => (
                      <Card key={test.id} className="bg-slate-50">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-medium">{test.name}</p>
                              <p className="text-sm text-muted-foreground capitalize">
                                Testing: {test.element_type}
                              </p>
                            </div>
                            <Badge variant={test.status === 'running' ? 'default' : test.status === 'completed' ? 'secondary' : 'outline'}>
                              {test.status === 'running' && <Play className="h-3 w-3 mr-1" />}
                              {test.status === 'completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {test.status}
                            </Badge>
                          </div>
                          
                          <div className="space-y-2">
                            {test.variants?.map(variant => {
                              const convRate = variant.views > 0 
                                ? ((variant.conversions / variant.views) * 100).toFixed(2) 
                                : 0;
                              const isWinner = test.winner === variant.name;
                              return (
                                <div 
                                  key={variant.id} 
                                  className={`flex items-center justify-between p-2 rounded ${isWinner ? 'bg-green-50 border border-green-200' : 'bg-white'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {isWinner && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                                    <span className="font-medium">{variant.name}</span>
                                  </div>
                                  <div className="flex items-center gap-4 text-sm">
                                    <span>{variant.views} views</span>
                                    <span>{variant.conversions} conv.</span>
                                    <Badge variant="outline">
                                      <Percent className="h-3 w-3 mr-1" />
                                      {convRate}%
                                    </Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {test.confidence_level > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">Statistical Confidence</span>
                                <span className="font-medium">{test.confidence_level}%</span>
                              </div>
                              <Progress value={test.confidence_level} className="h-2 mt-1" />
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FlaskConical className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">No A/B tests for this page yet</p>
                    <p className="text-sm text-muted-foreground">
                      Create A/B tests to optimize your landing page performance
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Device Breakdown</CardTitle>
                <CardDescription>Traffic distribution by device type</CardDescription>
              </CardHeader>
              <CardContent>
                {currentData?.device_breakdown ? (
                  <div className="space-y-4">
                    {[
                      { key: 'desktop', label: 'Desktop', icon: Monitor, color: '#3B82F6' },
                      { key: 'mobile', label: 'Mobile', icon: Smartphone, color: '#10B981' },
                      { key: 'tablet', label: 'Tablet', icon: Tablet, color: '#F59E0B' }
                    ].map(device => {
                      const count = currentData.device_breakdown[device.key] || 0;
                      const total = Object.values(currentData.device_breakdown).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={device.key} className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${device.color}20` }}>
                            <device.icon className="h-5 w-5" style={{ color: device.color }} />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <span className="font-medium">{device.label}</span>
                              <span className="text-muted-foreground">{count.toLocaleString()} ({percentage}%)</span>
                            </div>
                            <Progress value={parseFloat(percentage)} className="h-2" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                    No device data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Device Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                {currentData?.device_breakdown ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPie>
                      <Pie
                        data={[
                          { name: 'Desktop', value: currentData.device_breakdown.desktop || 0, fill: '#3B82F6' },
                          { name: 'Mobile', value: currentData.device_breakdown.mobile || 0, fill: '#10B981' },
                          { name: 'Tablet', value: currentData.device_breakdown.tablet || 0, fill: '#F59E0B' }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : null}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LandingPageAnalyticsPage;
